import { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Send, Search, X } from 'lucide-react';
import api from '../../services/api';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import SEOHead from '../../components/common/SEOHead';
import { usePageSeo } from '../../hooks/useSeoConfig';

// ── Contact form (last FAQ item) ─────────────────────────────────────────────
const CONTACT_ID = 'contact';

const ContactItem = ({ language, isOpen, onToggle }) => {
    const { user } = useAuth();
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [status, setStatus]   = useState(null); // 'success' | 'error'

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!subject.trim() || !message.trim()) return;
        setSending(true);
        setStatus(null);
        try {
            await api.post('/contact.php', {
                subject:   subject.trim(),
                message:   message.trim(),
                userName:  user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '',
                userEmail: user?.email || '',
            });
            setStatus('success');
            setSubject('');
            setMessage('');
        } catch {
            setStatus('error');
        } finally {
            setSending(false);
        }
    };

    const inp = "w-full px-4 py-3 text-sm border border-gray-200 dark:border-white/[0.08] rounded-xl bg-white dark:bg-white/[0.03] text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition";

    return (
        <div className="border-b border-gray-100 dark:border-white/5 last:border-b-0">
            <button
                onClick={onToggle}
                className="w-full flex items-center justify-between gap-4 px-2 py-5 text-left group"
            >
                <span className="font-semibold text-base leading-snug text-blue-600 dark:text-blue-400">
                    {language === 'en'
                        ? "Didn't find your answer? Contact us"
                        : "Tu n'as pas trouvé la réponse ? Contacte-nous"}
                </span>
                <span className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-all duration-200 ${
                    isOpen
                        ? 'bg-white dark:bg-white/15 text-gray-700 dark:text-gray-200'
                        : 'bg-gray-100 dark:bg-white/5 text-gray-400 dark:text-gray-500 group-hover:bg-gray-200 dark:group-hover:bg-white/10'
                }`}>
                    <svg className={`w-3.5 h-3.5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                    </svg>
                </span>
            </button>
            {isOpen && (
                <div className="px-2 pb-6">
                    <form onSubmit={handleSubmit} className="space-y-3">
                        <input
                            type="text"
                            value={subject}
                            onChange={e => setSubject(e.target.value)}
                            placeholder={language === 'fr' ? 'Sujet' : 'Subject'}
                            className={inp}
                            required
                        />
                        <textarea
                            value={message}
                            onChange={e => setMessage(e.target.value)}
                            placeholder={language === 'fr' ? 'Ton message...' : 'Your message...'}
                            rows={4}
                            className={inp + " resize-none"}
                            required
                        />
                        <div className="flex items-center justify-between">
                            {status === 'success' && (
                                <p className="text-xs font-medium text-green-600 dark:text-green-400">
                                    {language === 'fr' ? 'Message envoyé avec succès !' : 'Message sent successfully!'}
                                </p>
                            )}
                            {status === 'error' && (
                                <p className="text-xs font-medium text-red-600 dark:text-red-400">
                                    {language === 'fr' ? "Erreur lors de l'envoi. Réessayez." : 'Error sending. Please try again.'}
                                </p>
                            )}
                            {!status && <span />}
                            <button
                                type="submit"
                                disabled={sending || !subject.trim() || !message.trim()}
                                className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-xl disabled:opacity-50 transition"
                            >
                                <Send size={14} />
                                {sending
                                    ? (language === 'fr' ? 'Envoi...' : 'Sending...')
                                    : (language === 'fr' ? 'Envoyer' : 'Send')}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};

// ── Main FaqList ──────────────────────────────────────────────────────────────
const FaqList = () => {
    const location = useLocation();
    const { language } = useLanguage();
    const inDashboard = location.pathname.startsWith('/dashboard');

    const [items, setItems]               = useState([]);
    const [categories, setCategories]     = useState([]);
    const [selectedCategory, setSelected] = useState(null);
    const [openId, setOpenId]             = useState(null);
    const [loading, setLoading]           = useState(true);
    const [searchInput, setSearchInput]   = useState('');

    useEffect(() => { setSelected(null); setSearchInput(''); }, [language]);

    useEffect(() => {
        api.get('/faq.php', { params: { action: 'categories' } })
            .then(res => setCategories(res.data || []))
            .catch(() => {});
    }, []);

    useEffect(() => {
        setLoading(true);
        const params = { action: 'list', lang: language };
        if (selectedCategory) params.category = selectedCategory;
        api.get('/faq.php', { params })
            .then(res => setItems(res.data.items || []))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    }, [language, selectedCategory]);

    const categoryList = categories.map(c =>
        language === 'en' ? c.category_en : c.category_fr
    ).filter(Boolean);

    const toggleItem = (id) => setOpenId(prev => prev === id ? null : id);

    // Client-side search filter
    const filteredItems = useMemo(() => {
        const q = searchInput.trim().toLowerCase();
        if (!q) return items;
        return items.filter(item => {
            const question = (language === 'en' ? item.question_en : item.question_fr) || '';
            const answer   = (language === 'en' ? item.answer_en   : item.answer_fr)   || '';
            return question.toLowerCase().includes(q) || answer.toLowerCase().includes(q);
        });
    }, [items, searchInput, language]);

    // ── Inline JSX sections (NOT sub-components, to preserve input focus) ────

    const searchBar = (
        <div className="relative mb-6">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500 pointer-events-none" />
            <input
                type="text"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                placeholder={language === 'fr' ? 'Rechercher une question...' : 'Search a question...'}
                className="w-full pl-11 pr-10 py-3 text-sm bg-white dark:bg-white/5 border border-gray-200 dark:border-white/[0.08] rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition"
            />
            {searchInput && (
                <button
                    onClick={() => setSearchInput('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition"
                >
                    <X className="w-4 h-4" />
                </button>
            )}
        </div>
    );

    const categoryFilters = categoryList.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-10">
            <button
                onClick={() => setSelected(null)}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${
                    !selectedCategory
                        ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900'
                        : 'bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-white/50 hover:bg-gray-200 dark:hover:bg-white/10'
                }`}
            >
                {language === 'en' ? 'All' : 'Tous'}
            </button>
            {categoryList.map(cat => (
                <button
                    key={cat}
                    onClick={() => setSelected(cat)}
                    className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${
                        selectedCategory === cat
                            ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900'
                            : 'bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-white/50 hover:bg-gray-200 dark:hover:bg-white/10'
                    }`}
                >
                    {cat}
                </button>
            ))}
        </div>
    );

    const faqAccordion = (
        <>
            {loading && (
                <div className="flex justify-center py-20">
                    <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500" />
                </div>
            )}
            {!loading && filteredItems.length === 0 && (
                <div className="text-center py-20 text-gray-400">
                    <p className="text-lg">{language === 'en' ? 'No questions found.' : 'Aucune question trouvée.'}</p>
                </div>
            )}
            {!loading && (
                <div className="divide-y divide-gray-100 dark:divide-white/5">
                    {filteredItems.map(item => {
                        const question = language === 'en' ? item.question_en : item.question_fr;
                        const answer   = language === 'en' ? item.answer_en   : item.answer_fr;
                        const isOpen   = openId === item.idFaq;
                        return (
                            <div key={item.idFaq}>
                                <button
                                    onClick={() => toggleItem(item.idFaq)}
                                    className="w-full flex items-center justify-between gap-4 px-4 py-6 text-left group"
                                >
                                    <span className={`font-semibold text-base md:text-lg leading-snug transition-colors ${
                                        isOpen ? 'text-gray-900 dark:text-white' : 'text-gray-700 dark:text-white/85 group-hover:text-gray-900 dark:group-hover:text-white'
                                    }`}>
                                        {question || '—'}
                                    </span>
                                    <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
                                        isOpen
                                            ? 'bg-white dark:bg-white/20 text-gray-700 dark:text-white'
                                            : 'bg-gray-100 dark:bg-white/10 text-gray-500 dark:text-white/70 group-hover:bg-gray-200 dark:group-hover:bg-white/15'
                                    }`}>
                                        <svg className={`w-3.5 h-3.5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </span>
                                </button>
                                {isOpen && (
                                    <div className="px-2 pb-6">
                                        {/* Author */}
                                        <div className="flex items-center gap-3 mb-4">
                                            <img
                                                src="/img/application/explanationLogo.png"
                                                alt=""
                                                className="w-10 h-10 rounded-full object-cover"
                                            />
                                            <div>
                                                <p className="text-sm font-bold text-gray-900 dark:text-white leading-tight">
                                                    {language === 'fr' ? "L'équipe nizus" : 'nizus team'}
                                                </p>
                                            </div>
                                        </div>
                                        {/* Answer blockquote */}
                                        <div className="border-l-2 border-gray-300 dark:border-white/15 pl-5 ml-1">
                                            <p className="text-sm text-gray-700 dark:text-white/60 leading-relaxed whitespace-pre-line">
                                                {answer || '—'}
                                            </p>
                                        </div>
                                    </div>
                                )}
                        </div>
                        );
                    })}

                    {/* Contact form as last item */}
                    <ContactItem
                        language={language}
                        isOpen={openId === CONTACT_ID}
                        onToggle={() => toggleItem(CONTACT_ID)}
                    />
                </div>
            )}
        </>
    );

    const banner = (
        <div className="relative overflow-hidden rounded-[2rem] p-10 h-64 bg-gradient-to-br from-[#00695c] via-[#004d40] to-[#1a237e] border border-white/5 mb-8 shadow-xl text-white">
            <div
                className="absolute inset-0 opacity-20 pointer-events-none"
                style={{ backgroundImage: 'linear-gradient(90deg, transparent 50%, rgba(0,0,0,0.5) 50%)', backgroundSize: '4px 100%' }}
            />
            <div className="relative z-10 h-full flex flex-col justify-between">
                <div className="flex items-center gap-4">
                    <div className="p-3 bg-white/20 rounded-2xl">
                        <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <h1 className="text-4xl font-black text-white tracking-tight">{language === 'en' ? 'Help' : 'Aide'}</h1>
                </div>
                <p className="text-white/60 text-lg max-w-sm leading-snug">
                    {language === 'en'
                        ? 'Frequently asked questions about nizus and pilot licences, and contact form.'
                        : 'Questions fréquentes sur nizus et les licences de pilote, et formulaire de contact.'}
                </p>
            </div>
            <div className="absolute right-[-40px] bottom-[-40px] opacity-20 pointer-events-none">
                <svg className="w-72 h-72 text-white stroke-[0.5]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            </div>
        </div>
    );

    // ── Render ───────────────────────────────────────────────────────────────
    // Layout (container width, padding, background) is provided by the parent —
    // PublicContentLayout for /faq, AccountLayout for /dashboard/account/faq.
    const seo = usePageSeo('page_faq');
    const faqJsonLd = !inDashboard && filteredItems.length > 0 ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": filteredItems.map(item => ({
            "@type": "Question",
            "name": language === 'fr' ? item.question_fr : item.question_en,
            "acceptedAnswer": {
                "@type": "Answer",
                "text": (language === 'fr' ? item.answer_fr : item.answer_en)?.replace(/<[^>]*>/g, '') || '',
            }
        }))
    } : undefined;

    return (
        <>
            {!inDashboard && (
                <SEOHead
                    title={language === 'fr' ? (seo.title_fr || 'FAQ - Questions fréquentes') : (seo.title_en || 'FAQ - Frequently Asked Questions')}
                    description={language === 'fr' ? seo.desc_fr : seo.desc_en}
                    keywords={language === 'fr' ? seo.keywords_fr : seo.keywords_en}
                    canonical="/faq"
                    jsonLd={faqJsonLd}
                />
            )}
            {banner}
            {searchBar}
            {categoryFilters}
            {faqAccordion}
        </>
    );
};

export default FaqList;
